import React, { useState, useCallback, useEffect } from 'react';
import { Table } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { getTransferReportApi, exportTransferReportApi, uniformErrorMessage } from 'api/services/uniformManagement';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, transferStatusOptions } from '../../constants';
import ExcelExportButton from 'components/ExcelExportButton';
import ReportFilterBar, { cleanParams } from '../ReportFilterBar';
import useStickyIdentity from '../../components/useStickyIdentity';
import { UNIFORM_LANE, truncatedText } from '../../components/columns';

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

const FIELDS = ['dateRange', 'sourceWarehouse', 'destinationWarehouse', 'item', 'size', 'status', 'search'];

/** A transfer is defined by its route and its date, so the period and the two
 *  ends of the move lead. Source and destination are kept together on purpose:
 *  splitting the pair across the disclosure would read as a broken control far
 *  more than hiding both would. `search` is the one arguable omission — it is
 *  a lookup tool rather than a reporting axis, and when it is set the
 *  collapsed bar still names it. */
const PRIMARY_FIELDS = ['dateRange', 'sourceWarehouse', 'destinationWarehouse'];

/** Line-level stock transfer report — one row per transfer line, not per
 *  transfer, so the same transfer can appear more than once. */
const TransferReport = ({ itemOptions, itemOptionsLoading }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [draft, setDraft] = useState({});
  const [applied, setApplied] = useState({});

  // Guarded with `ignore` so a slow response for filters the user has since
  // changed can't clobber newer results (including the loading-state reset).
  const fetchData = useCallback(() => {
    let ignore = false;
    setLoading(true);
    const params = { ...cleanParams(applied), page: pageSettings.page, page_size: pageSettings.pageSize };
    getTransferReportApi(params).then((response) => {
      if (ignore) return;
      setData(response.data?.results || []);
      setPageSettings((prev) => ({ ...prev, total: response.data?.count || 0 }));
    }).catch((e) => {
      if (ignore) return;
      toast.error(uniformErrorMessage(e, 'Failed to load transfer report'), toastOpts);
    }).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [applied, pageSettings.page, pageSettings.pageSize]);

  useEffect(() => fetchData(), [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleApply = () => {
    setApplied(draft);
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const handleClear = () => {
    setDraft({});
    setApplied({});
    setPageSettings((prev) => ({ ...prev, page: 1 }));
  };

  const pinLeft = useStickyIdentity();

  const columns = [
    // Pinned: ten columns, and the reference is the only identifying cell.
    { title: 'Reference', dataIndex: 'reference', ...pinLeft(170) },
    { title: 'Date', dataIndex: 'transfer_date', render: (d) => formatDateHandler(d) },
    { title: 'From', dataIndex: 'source_warehouse_name' },
    { title: 'To', dataIndex: 'destination_warehouse_name' },
    { title: 'Uniform', dataIndex: 'uniform_item_name', ...truncatedText(UNIFORM_LANE) },
    { title: 'Size', dataIndex: 'size_code', render: (v) => v || '-' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Status', dataIndex: 'status', render: (s) => <span style={getStatusBadge(s)}>{s}</span> },
    { title: 'PO', dataIndex: 'po_number', render: (v) => v || '-' },
    { title: 'Transferred By', dataIndex: 'initiated_by_name', render: (v) => v || '-' },
  ];

  return (
    <div>
      <div className="d-flex justify-content-end mb-2">
        <ExcelExportButton framed apiFn={exportTransferReportApi} params={cleanParams(applied)} filename="Uniform Transfers.xlsx" />
      </div>
      <ReportFilterBar
        fields={FIELDS} primaryFields={PRIMARY_FIELDS}
        value={draft} appliedValue={applied} onChange={setDraft} statusOptions={transferStatusOptions}
        itemOptions={itemOptions} itemOptionsLoading={itemOptionsLoading}
        searchPlaceholder="Search reference, PO number..."
        onApply={handleApply} onClear={handleClear}
      />
      {/* No stable per-line id in the report row (only the parent transfer_id),
          so rowKey folds in the row index to stay unique within the page. */}
      <Table columns={columns} dataSource={data} rowKey={(r, i) => `${r.transfer_id}-${i}`} loading={loading}
        pagination={{
          total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
          pageSizeOptions, itemRender, showSizeChanger: true
        }}
        onChange={handleChange} scroll={{ x: 'max-content' }} />
    </div>
  );
};

export default TransferReport;
